/**
 * unifyRoadmapItems.js
 *
 * Convert every source-type (promise, workbook, safety_net, feature,
 * activity, ledger) into a single uniform `UnifiedRoadmapItem` shape so the
 * master-detail layout can render them through one list-row component and
 * type-dispatch the detail pane via `item.type`.
 *
 * Cycle-5c+25-fix8 (Phase 12E): authored per Owner direction —
 *   "one big list and i can use the filters at top to break it down;
 *    separate sections down the page make it hard to navigate and prioritise"
 *
 *   Owner clarification on AgentSidebar question:
 *   "a list of all roadmap items on the left, that when clicked open up
 *    the page on the right so not to go to new tab, would be handy"
 *
 * Shape:
 *   {
 *     id:            "<type>:<original_id>",  // unique across sources
 *     type:          "promise" | "workbook" | "safety_net" | "feature"
 *                  | "activity" | "ledger",
 *     title:         string,
 *     summary:       string,
 *     agents:        string[],                // lowercased agent codes
 *     effort_label:  string,                  // quick-win | short-focus | etc
 *     tier:          string | null,           // P0..P5 OR current/next/future
 *     waiting_on:    "owner" | "agent" | "both" | "done" | null,
 *     status:        "open" | "closed",
 *     mp_rec_rank:   number,                  // lower = more actionable
 *     source_path:   string,
 *     last_decision: object | null,
 *     search_text:   string,                  // lower(title + summary) for `?q=`
 *     raw:           object,                  // original full object for detail render
 *   }
 */

function normaliseAgents(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((a) => String(a || "").toLowerCase().trim())
    .filter((a) => a && a !== "owner" && a !== "mp")
    .slice(0, 4);
}

function buildSearchText(...parts) {
  return parts
    .filter(Boolean)
    .map((s) => String(s).toLowerCase())
    .join(" \u00b7 ");
}

function buildKey(type, id) {
  return `${type}:${id}`;
}

// ── MP-curated pinning (cycle-5c+25-fix11; cycle-5c+25-fix17 generalised) ─
//
// Two related mechanisms share this scaffold:
//   (a) Tone-check pinning during multi-tranche authoring (cycle-5c+25-fix11):
//       MP pins a small set of newly-authored cards to the top for Owner
//       eyeball-review, then clears.
//   (b) MP-curated "next sit-down" pinning (cycle-5c+25-fix17):
//       When MP chat-suggests specific refs ("here are 4 cards waiting on
//       you that you can knock off in 30 min"), MP pins those exact IDs
//       so they float to the very top regardless of mp_rec_rank.
//       Solves the Owner UX gap "if you are sorting the list by priority,
//       why do the suggested refs you are prompting me to do now not right
//       at, or at least near the top?" — MP-rec-rank is an architectural-
//       urgency axis; MP's chat suggestions are a curated quick-win axis;
//       they don't always coincide, so a separate pin handles the curation.
//
// Both pin-scopes use the same negative-mp_rec_rank override + visible
// badge in the list row. The pin is TEMPORARY; the Owner clears it by
// either acting on the items or telling MP to clear it.
//
// Order inside the pin is stable: first ID gets rank -1000, second -999,
// and so on, so the array order maps directly to top-of-list order.
const TONE_CHECK_PINNED_IDS = [
  // 2026-05-24 cycle-5c+25-fix17 (later same session) — Owner declined the
  // MP-curated pin mechanism: "i think architectural priority is the best
  // way forward. I think your suggestions in chat should obviously stay
  // relevant to the topic we are working on at the time." Pin cleared
  // accordingly. The mechanism is left in place (zero-impact when empty)
  // for future tone-check tranches or any case where Owner explicitly
  // asks for a curated surfacing — default is empty.
];
const TONE_CHECK_PIN_RANK_BY_ID = TONE_CHECK_PINNED_IDS.reduce(
  (acc, id, idx) => {
    acc[id] = -1000 + idx;
    return acc;
  },
  {},
);

function applyToneCheckPin(item, originalId) {
  if (originalId && TONE_CHECK_PIN_RANK_BY_ID[originalId] !== undefined) {
    item.mp_rec_rank = TONE_CHECK_PIN_RANK_BY_ID[originalId];
    item.tone_check_pinned = true;
  }
  return item;
}

// ── Adapters ──────────────────────────────────────────────────────────────

const CLOSED_TITLE_RE = /^\s*(?:\u2705\s*)?(CLOSED|RESOLVED|DONE|RETIRED)\b/i;

function isClosedByTitle(title) {
  if (!title) return false;
  return CLOSED_TITLE_RE.test(title);
}

function fromPromise(p, decisionsByKey) {
  // Backend `/api/promises/by-audience` returns: id, title, owner, priority,
  // gate, notes_summary, notes_full, waiting_on, agents_mentioned,
  // effort_label, tier, mp_rec_rank. There is no `is_closed` boolean \u2014
  // closed rows are flagged by a "CLOSED " / "RESOLVED " / "DONE " prefix
  // in the title cell.
  const id = p.id || p.row_id || "";
  const title = p.title || id || "(untitled)";
  const last_decision = decisionsByKey?.[`promise:${id}`] || null;
  let status;
  if (p.is_closed || isClosedByTitle(title)) {
    status = "closed";
  } else if (last_decision) {
    // Owner has logged a decision \u2014 card has been actioned and drops out
    // of the default "Open" queue. cycle-5c+25-fix10 per Owner direction:
    // "it needs to get marked as complete and removed from list automatically".
    status = "decided";
  } else {
    status = "open";
  }
  return applyToneCheckPin({
    id: buildKey("promise", id),
    type: "promise",
    ref: p.ref || "",  // cycle-5c+25-fix15 — shorthand chat-reference (e.g. "C9")
    // cycle-5c+25-fix17 — hard-bucket "Owner-actionable now" above "agent-blocked"
    // in the default sort, so the things the Owner can action right now are
    // always at the top of the list (per Owner UX feedback "if you are sorting
    // the list by priority, why do the suggested refs you are prompting me to
    // do now not right at, or at least near the top?").
    owner_actionable: Boolean(p.owner_actionable),
    title,
    summary: p.notes_summary || p.notes_full || p.notes || p.summary || "",
    agents: normaliseAgents(p.agents_mentioned || p.agents || []),
    effort_label: p.effort_label || "short-focus",
    // Prefer the explicit P-tier from `priority` (e.g. "P3"); fall back to the
    // current/next/future bucket from `tier` if priority is absent.
    tier: (p.priority || "").trim().split(/\s+/)[0] || p.tier || null,
    waiting_on: p.waiting_on || p.audience || null,
    status,
    mp_rec_rank: p.mp_rec_rank ?? 999,
    source_path: p.source_path || "OPEN_PROMISES.md",
    last_decision,
    // cycle-5c+25-fix18 — include BOTH the bare ref ("C11") and the bracketed
    // chat-form ("[C11]") in search_text. Owner copy-pastes the bracketed
    // form straight out of MP's chat prompts ("here are 4 cards waiting on
    // you — [C9], [C11], [C14], [C22]") into the search bar. Without the
    // bracketed alias the literal substring `[c11]` returned zero hits and
    // the user couldn't surface the card. The filter ALSO strips brackets
    // defensively in applyUnifiedFilters as a belt-and-braces fallback.
    search_text: buildSearchText(
      title,
      id,
      p.ref,
      p.ref ? `[${p.ref}]` : "",
      p.notes_summary,
      p.gate,
      ...(p.agents_mentioned || []),
    ),
    raw: p,
  }, id);
}

function fromWorkbook(w, decisionsByKey, workbookAcksByKey) {
  const id = w.id || "";
  const title = w.title || id || "(untitled workbook)";
  const last_decision = decisionsByKey?.[`workbook:${id}`] || null;
  // cycle-5c+25-fix13: a workbook is also "decided" if the Owner has
  // submitted a pick-ack file via /api/picks/submit (lands in MP_INBOX/
  // not OWNER_DECISIONS/, so it isn't covered by decisionsByKey).
  const workbookAck = workbookAcksByKey?.[id] || null;
  let status;
  if (w.is_resolved || isClosedByTitle(title)) status = "closed";
  else if (last_decision || workbookAck) status = "decided";
  else status = "open";
  // Synthesise a last_decision-shaped record from the workbook ack so the
  // list row's "Last:" indicator can render the submission timestamp.
  const synthesised_last_decision = last_decision || (workbookAck
    ? {
        timestamp: workbookAck.submitted_at,
        choice_label: workbookAck.accept_all
          ? "Accepted all MP recommendations"
          : "Picks submitted",
        free_text: "",
        decided_by_email: workbookAck.submitted_by,
        source: "workbook-pick-ack",
      }
    : null);
  return {
    id: buildKey("workbook", id),
    type: "workbook",
    title,
    summary: w.summary || "",
    agents: normaliseAgents(w.agents_mentioned || []),
    effort_label: w.effort_label || "long-focus",
    tier: w.tier || null,
    waiting_on: w.is_owner_facing ? "owner" : "agent",
    status,
    mp_rec_rank: w.mp_rec_rank ?? 999,
    source_path: w.path || `MP_INBOX/${id}.md`,
    last_decision: synthesised_last_decision,
    search_text: buildSearchText(title, w.summary, ...(w.agents_mentioned || [])),
    raw: w,
  };
}

function fromSafetyNet(sn, agentKey, decisionsByKey) {
  // Drift-risk items live inside a per-agent rollup; flatten them.
  const id = sn.id || sn.fact_id || `${agentKey}-${sn.title?.slice(0, 30) || "sn"}`;
  const last_decision = decisionsByKey?.[`safety_net:${id}`] || null;
  let status;
  if (sn.is_swept) status = "closed";
  else if (last_decision) status = "decided";
  else status = "open";
  return {
    id: buildKey("safety_net", id),
    type: "safety_net",
    title: sn.title || `Safety-net: ${id}`,
    summary: sn.summary || sn.detail || sn.soak_window || "",
    agents: agentKey ? [agentKey.toLowerCase()] : [],
    effort_label: sn.effort_label || "quick-win",
    tier: sn.tier || sn.severity_tier || null,
    waiting_on: sn.waiting_on || "agent",
    status,
    mp_rec_rank: sn.mp_rec_rank ?? 999,
    source_path: sn.source_path || "architecture-canon/INFRA_REFERENCES.md",
    last_decision,
    search_text: buildSearchText(sn.title, sn.summary, agentKey),
    raw: sn,
  };
}

function fromActivity(act, agentKey, decisionsByKey) {
  const id = act.id || `${agentKey}-${act.title?.slice(0, 30) || "act"}`;
  const last_decision = decisionsByKey?.[`activity:${id}`] || null;
  let status;
  if (act.is_ratified || act.is_closed) status = "closed";
  else if (last_decision) status = "decided";
  else status = "open";
  return {
    id: buildKey("activity", id),
    type: "activity",
    title: act.title || `Activity: ${id}`,
    summary: act.summary || act.detail || "",
    agents: agentKey ? [agentKey.toLowerCase()] : [],
    effort_label: act.effort_label || "short-focus",
    tier: act.tier || null,
    waiting_on: act.waiting_on || (act.is_ratified ? "done" : "agent"),
    status,
    mp_rec_rank: act.mp_rec_rank ?? 999,
    source_path: act.source_path || "",
    last_decision,
    search_text: buildSearchText(act.title, act.summary, agentKey),
    raw: act,
  };
}

function fromFeature(feat, appKey, decisionsByKey) {
  const id = feat.id || feat.feat_id || `${appKey}-${feat.title?.slice(0, 30) || "feat"}`;
  const last_decision = decisionsByKey?.[`feature:${id}`] || null;
  let status;
  if (feat.is_done || feat.is_closed) status = "closed";
  else if (last_decision) status = "decided";
  else status = "open";
  return {
    id: buildKey("feature", id),
    type: "feature",
    title: feat.title || `Feature: ${id}`,
    summary: feat.summary || feat.detail || "",
    agents: appKey ? [appKey.toLowerCase()] : [],
    effort_label: feat.effort_label || "long-focus",
    tier: feat.tier || null,
    waiting_on: feat.waiting_on || "agent",
    status,
    mp_rec_rank: feat.mp_rec_rank ?? 999,
    source_path: feat.source_path || `roadmaps/${appKey}-roadmap.md`,
    last_decision,
    search_text: buildSearchText(feat.title, feat.summary, appKey),
    raw: feat,
  };
}

function fromLedger(row, idx) {
  // Ledger rows are CLOSED by definition (decisions already taken).
  const id = row.item_id || row.id || `row-${idx}`;
  return {
    id: buildKey("ledger", id),
    type: "ledger",
    title: row.title || row.decision_summary || row.session || `Ledger row ${idx + 1}`,
    summary: row.decision || row.summary || "",
    agents: normaliseAgents(row.agents || []),
    effort_label: "short-focus",
    tier: null,
    waiting_on: "done",
    status: "closed",
    mp_rec_rank: 9999 + idx, // ledger goes last by default
    source_path: row.link || "LEDGER.md",
    last_decision: null,
    search_text: buildSearchText(row.title, row.decision, row.session),
    raw: row,
  };
}

// ── Main unifier ──────────────────────────────────────────────────────────

/**
 * Combine all roadmap data sources into one flat array of UnifiedRoadmapItem.
 * Items are pre-sorted by mp_rec_rank ascending.
 *
 * Args:
 *   sources = {
 *     promises:       PromisesByAudience,
 *     workbooks:      WorkbookView[],
 *     drift:          DriftRiskPayload (federation),
 *     federation:     FederationPayload,
 *     perAppRoadmaps: PerAppRoadmapPayload,
 *     ledger:         LedgerRow[],
 *     decisionsByKey: { "<type>:<id>": OwnerDecisionView },
 *   }
 */
export function unifyRoadmapItems(sources) {
  const {
    promises = {},
    workbooks = [],
    drift = null,
    federation = null,
    perAppRoadmaps = null,
    ledger = [],
    decisionsByKey = {},
    workbookAcksByKey = {},
    viewAuditByItem = {},
    workAuditByItem = {},
  } = sources;

  const out = [];

  // Promises (owner + agent + both audiences). Avoid double-counting `both`.
  const seenPromiseIds = new Set();
  const pushPromise = (arr) => {
    for (const p of arr || []) {
      const id = p.row_id || p.id || "";
      if (!id || seenPromiseIds.has(id)) continue;
      seenPromiseIds.add(id);
      out.push(fromPromise(p, decisionsByKey));
    }
  };
  pushPromise(promises.owner);
  pushPromise(promises.agent);
  pushPromise(promises.both);
  pushPromise(promises.none);

  // Workbooks
  for (const w of workbooks || []) {
    out.push(fromWorkbook(w, decisionsByKey, workbookAcksByKey));
  }

  // Drift-risk (safety-nets): flatten the per-agent rollup
  if (drift?.per_agent) {
    for (const ag of drift.per_agent) {
      const agentKey = ag.agent_key || ag.agent || "";
      for (const sn of ag.items || ag.safety_nets || []) {
        out.push(fromSafetyNet(sn, agentKey, decisionsByKey));
      }
    }
  }

  // Federation per-agent activity
  if (federation?.per_agent) {
    for (const ag of federation.per_agent) {
      const agentKey = ag.agent_key || ag.agent || "";
      for (const act of ag.activity_items || ag.items || []) {
        out.push(fromActivity(act, agentKey, decisionsByKey));
      }
    }
  }

  // Per-app roadmaps (features)
  if (perAppRoadmaps?.per_app) {
    for (const app of perAppRoadmaps.per_app) {
      const appKey = app.app_key || app.app || "";
      // Per-app roadmaps come in tier buckets: current / next / future
      const tierBuckets = ["current", "next", "future"];
      for (const tier of tierBuckets) {
        for (const feat of app[tier] || []) {
          out.push(fromFeature({ ...feat, tier }, appKey, decisionsByKey));
        }
      }
    }
  }

  // Ledger (last)
  (ledger || []).forEach((row, idx) => out.push(fromLedger(row, idx)));

  // ── Phase 14 / Batch 3 / cycle-5c+25-fix20 — staleness enrichment ──────
  //
  // For every unified item, attach:
  //   - last_viewed_at  (Mongo view_audit, explicit "I worked on this")
  //   - last_worked_at  (canon-derived: latest decision / pick-ack ISO ts)
  //   - last_touched_at (= max of the two; "" if neither is present)
  //   - days_since_touch (number) — Infinity if never-touched
  //   - freshness_tier ("fresh"|"aging"|"stale"|"never")
  //
  // The key into both maps is the same `<type>:<original_id>` shape used
  // by `decisionsByKey` / `workbookAcksByKey`. For workbook items we use
  // the bare workbook_id (matching the backend key shape).
  const now = Date.now();
  const parseIso = (s) => {
    if (!s) return null;
    const t = Date.parse(s);
    return Number.isNaN(t) ? null : t;
  };
  const auditKeyFor = (item) => {
    // item.id is "<type>:<original_id>" so it already matches the backend
    // key shape (`promise:<id>`, `workbook:<id>`, etc.).
    return item.id;
  };
  for (const it of out) {
    const k = auditKeyFor(it);
    const va = viewAuditByItem?.[k] || null;
    const wa = workAuditByItem?.[k] || null;
    const viewTs = parseIso(va?.last_viewed_at) ?? null;
    const workTs = parseIso(wa?.last_worked_at) ?? null;
    let lastTouchedAt = "";
    let lastTouchedMs = null;
    if (viewTs !== null && workTs !== null) {
      lastTouchedMs = Math.max(viewTs, workTs);
      lastTouchedAt = viewTs >= workTs ? va.last_viewed_at : wa.last_worked_at;
    } else if (viewTs !== null) {
      lastTouchedMs = viewTs;
      lastTouchedAt = va.last_viewed_at;
    } else if (workTs !== null) {
      lastTouchedMs = workTs;
      lastTouchedAt = wa.last_worked_at;
    }
    const daysSince = lastTouchedMs === null
      ? Infinity
      : Math.floor((now - lastTouchedMs) / (1000 * 60 * 60 * 24));
    let tier;
    if (lastTouchedMs === null) tier = "never";
    else if (daysSince < 7) tier = "fresh";
    else if (daysSince <= 30) tier = "aging";
    else tier = "stale";

    it.last_viewed_at = va?.last_viewed_at || "";
    it.viewer_email = va?.viewer_email || "";
    it.last_worked_at = wa?.last_worked_at || "";
    it.work_source = wa?.source || "";
    it.last_touched_at = lastTouchedAt;
    it.days_since_touch = daysSince;
    it.freshness_tier = tier;
  }

  // Stable sort. cycle-5c+25-fix17: primary key is `owner_actionable`
  // (truthy items first — they're the things the Owner can do RIGHT NOW),
  // then by `mp_rec_rank` ascending, then by id.
  out.sort((a, b) => {
    const aOA = a.owner_actionable ? 0 : 1;
    const bOA = b.owner_actionable ? 0 : 1;
    if (aOA !== bOA) return aOA - bOA;
    if (a.mp_rec_rank !== b.mp_rec_rank) return a.mp_rec_rank - b.mp_rec_rank;
    return a.id.localeCompare(b.id);
  });

  return out;
}

// ── Filter shape + apply ──────────────────────────────────────────────────

export const DEFAULT_FILTERS = {
  search: "",
  status: "open",  // hide closed by default per Owner Q2 (cycle-5c+25-fix8)
  type: "all",
  tier: "all",
  agent: "all",
  effort: "all",
  waiting_on: "all",
  // cycle-5c+25-fix20 (Phase 14 / Batch 3): Owner Q4=(c) — staleness filter.
  // 0 = "off" (no staleness filter applied). Otherwise: only show items
  // whose `days_since_touch >= stale_min_days`. Common presets surfaced
  // in the UI: 7 / 14 / 30 / 60.
  stale_min_days: 0,
  sort: "mp_rec",  // default — order by MP-rec rank per Owner Q3 (cycle-5c+25-fix8)
};

const TIER_GROUP_MAP = {
  current: ["current", "P0", "P1"],
  next: ["next", "P2", "P3"],
  future: ["future", "P4", "P5"],
};

export function applyUnifiedFilters(items, filters) {
  if (!Array.isArray(items)) return [];
  const f = { ...DEFAULT_FILTERS, ...filters };
  // cycle-5c+25-fix18 — also build a "stripped" query that drops surrounding
  // brackets/whitespace, so Owner pasting "[C11]" (the chat-prompt form MP
  // uses) matches search_text entries that store only the bare "c11" form.
  // The two-pronged match (raw OR stripped) makes the filter forgiving for
  // any of: "C11", "c11", "[C11]", " [C11] ".
  const qRaw = (f.search || "").toLowerCase().trim();
  const qStripped = qRaw.replace(/^[\s[\]]+|[\s[\]]+$/g, "");

  // cycle-5c+25-fix19 — Owner-direct UX rule: when the search query is an
  // EXACT [Cn] ref pattern (e.g. "c11", "[C11]", " [c11] "), bypass every
  // category filter (status / type / tier / agent / effort / waiting_on)
  // and match the row purely on `item.ref`. Rationale: typing a specific
  // ref code is an Owner intent of "show me THIS card, regardless of
  // category". Without this override, decided cards (auto-hidden by the
  // default `status=open` filter) become unreachable by search — which is
  // exactly the bug that surfaced [C11] as "still not appearing" even
  // after the bracketed-form search_text fix landed.
  const refOnlyMatch = qStripped && /^c\d+$/i.test(qStripped);
  if (refOnlyMatch) {
    const targetRef = qStripped.toUpperCase();
    return items.filter((it) => (it.ref || "").toUpperCase() === targetRef);
  }

  return items.filter((it) => {
    if (f.status !== "all" && it.status !== f.status) return false;
    if (f.type !== "all" && it.type !== f.type) return false;
    if (f.tier !== "all") {
      const tierGroup = TIER_GROUP_MAP[f.tier];
      if (tierGroup) {
        if (!tierGroup.includes(it.tier)) return false;
      } else if (it.tier !== f.tier) {
        return false;
      }
    }
    if (f.agent !== "all" && !it.agents.includes(f.agent)) return false;
    if (f.effort !== "all" && it.effort_label !== f.effort) return false;
    if (f.waiting_on !== "all" && it.waiting_on !== f.waiting_on) return false;
    // cycle-5c+25-fix20 (Phase 14 / Batch 3): staleness filter — only show
    // items that haven't been touched in at least `stale_min_days` days.
    // Never-touched items have `days_since_touch === Infinity` so they
    // always pass this filter when it's active.
    if (f.stale_min_days && f.stale_min_days > 0) {
      const dst = it.days_since_touch;
      if (typeof dst !== "number" || dst < f.stale_min_days) return false;
    }
    if (qRaw) {
      const hay = it.search_text || "";
      // Prefer the raw (bracketed) form for precision — e.g. "[C9]" should
      // hit "[c9]" in search_text but NOT "[c90]" / "[c91]". Only fall back
      // to the bracket-stripped form when the raw form misses, so accidental
      // bracket inclusion still surfaces the card.
      if (hay.includes(qRaw)) {
        return true;
      }
      if (qStripped && qStripped !== qRaw && hay.includes(qStripped)) {
        return true;
      }
      return false;
    }
    return true;
  });
}
